import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc
} from "firebase/firestore";
import {
  getDownloadURL,
  ref,
  uploadBytesResumable
} from "firebase/storage";
import { db, storage } from "./firebase";

const shouldResolveStoragePath = (value) =>
  typeof value === "string" &&
  value.length > 0 &&
  !value.startsWith("http://") &&
  !value.startsWith("https://") &&
  !value.startsWith("data:") &&
  !value.startsWith("blob:");

const normalizeStoragePath = (value) => {
  const decoded = decodeURIComponent(value).replace(/^\/+/, "");
  const withoutQuery = decoded.split("?")[0];
  return withoutQuery;
};

export const resolveImageUrl = async (value) => {
  if (!shouldResolveStoragePath(value)) {
    return value;
  }

  try {
    return await getDownloadURL(ref(storage, normalizeStoragePath(value)));
  } catch (error) {
    return value;
  }
};

const compressImageForUpload = (file, maxSide = 1800, quality = 0.8) =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Unable to process image"));
          return;
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Unable to compress image"));
              return;
            }
            resolve(blob);
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = () => reject(new Error("Invalid image file"));
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const sortByCreatedAtDesc = (items) =>
  [...items].sort((a, b) => {
    const aTime = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : new Date(a.createdAt || 0).getTime();
    const bTime = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });

const mapDoc = (snapshot) => ({ id: snapshot.id, ...snapshot.data() });

export const getBookings = async () => {
  const snapshot = await getDocs(collection(db, "bookings"));
  return sortByCreatedAtDesc(snapshot.docs.map(mapDoc));
};

export const createBooking = async (payload) => {
  const docRef = await addDoc(collection(db, "bookings"), {
    ...payload,
    status: "pending",
    createdAt: serverTimestamp()
  });
  return docRef.id;
};

export const updateBookingStatus = async (bookingId, status) => {
  await updateDoc(doc(db, "bookings", bookingId), { status });
};

export const deleteBooking = async (bookingId) => {
  await deleteDoc(doc(db, "bookings", bookingId));
};

export const getImages = async () => {
  const snapshot = await getDocs(collection(db, "images"));
  const items = await Promise.all(
    snapshot.docs.map(async (item) => {
      const data = mapDoc(item);
      return {
        ...data,
        url: await resolveImageUrl(data.url)
      };
    })
  );
  return sortByCreatedAtDesc(items);
};

export const createImage = async (payload) => {
  const docRef = await addDoc(collection(db, "images"), {
    ...payload,
    createdAt: serverTimestamp()
  });
  return docRef.id;
};

export const updateImage = async (imageId, payload) => {
  await updateDoc(doc(db, "images", imageId), payload);
};

export const deleteImage = async (imageId) => {
  await deleteDoc(doc(db, "images", imageId));
};

export const getAlbums = async () => {
  const snapshot = await getDocs(collection(db, "albums"));
  const items = await Promise.all(
    snapshot.docs.map(async (item) => {
      const data = mapDoc(item);
      return {
        ...data,
        coverImage: await resolveImageUrl(data.coverImage),
        images: Array.isArray(data.images)
          ? await Promise.all(data.images.map((image) => resolveImageUrl(image)))
          : []
      };
    })
  );
  return sortByCreatedAtDesc(items);
};

export const createAlbum = async (payload) => {
  const docRef = await addDoc(collection(db, "albums"), {
    ...payload,
    createdAt: serverTimestamp()
  });
  return docRef.id;
};

export const deleteAlbum = async (albumId) => {
  await deleteDoc(doc(db, "albums", albumId));
};

export const getVideos = async () => {
  const snapshot = await getDocs(collection(db, "videos"));
  const items = await Promise.all(
    snapshot.docs.map(async (item) => {
      const data = mapDoc(item);
      return {
        ...data,
        thumbnail: await resolveImageUrl(data.thumbnail)
      };
    })
  );
  return sortByCreatedAtDesc(items);
};

export const createVideo = async (payload) => {
  const docRef = await addDoc(collection(db, "videos"), {
    ...payload,
    createdAt: serverTimestamp()
  });
  return docRef.id;
};

export const deleteVideo = async (videoId) => {
  await deleteDoc(doc(db, "videos", videoId));
};

export const getHomepageSettings = async () => {
  const snapshot = await getDoc(doc(db, "site_settings", "homepage"));
  if (!snapshot.exists()) {
    return { heroSliderImages: [] };
  }
  const data = snapshot.data();
  return {
    ...data,
    heroSliderImages: Array.isArray(data.heroSliderImages)
      ? await Promise.all(data.heroSliderImages.map((image) => resolveImageUrl(image)))
      : []
  };
};

export const saveHomepageSettings = async (payload) => {
  await setDoc(doc(db, "site_settings", "homepage"), payload, { merge: true });
};

export const syncDefaultContent = async ({ images, albums, videos }) => {
  const imageSnapshot = await getDocs(collection(db, "images"));
  const albumSnapshot = await getDocs(collection(db, "albums"));
  const videoSnapshot = await getDocs(collection(db, "videos"));

  const existingImageUrls = new Set(imageSnapshot.docs.map((item) => item.data().url));
  const existingAlbumTitles = new Set(albumSnapshot.docs.map((item) => item.data().title));
  const existingVideoTitles = new Set(videoSnapshot.docs.map((item) => item.data().title));

  for (const image of images) {
    if (!existingImageUrls.has(image.url)) {
      await createImage({
        url: image.url,
        category: image.category,
        albumId: image.albumId || null
      });
    }
  }

  for (const album of albums) {
    if (!existingAlbumTitles.has(album.title)) {
      await createAlbum({
        title: album.title,
        titleFont: album.titleFont || "playfair",
        coverImage: album.coverImage,
        date: album.date,
        location: album.location,
        photoCount: album.photoCount,
        images: album.images
      });
    }
  }

  for (const video of videos) {
    if (!existingVideoTitles.has(video.title)) {
      await createVideo(video);
    }
  }
};

export const uploadImageToStorage = (file, onProgress) =>
  new Promise(async (resolve, reject) => {
    try {
      const compressedFile = await compressImageForUpload(file);
      const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
      const storageRef = ref(storage, `uploads/${filename}`);
      const task = uploadBytesResumable(storageRef, compressedFile, {
        contentType: "image/jpeg"
      });

      task.on(
        "state_changed",
        (snapshot) => {
          const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          if (onProgress) onProgress(percent);
        },
        reject,
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        }
      );
    } catch (error) {
      reject(error);
    }
  });
